// The Users page's table and count row: a user per row with their role (a select when the
// viewer may assign roles), dates, status and the per-user actions their permissions allow.
import { CheckCircle, DeviceMobile, EnvelopeSimple, Key, LinkSimple, Prohibit, ShieldStar, UsersThree } from "@phosphor-icons/react";
import { ActionIcon, Switch } from "rizzui";

import type { User } from "../api/client";
import { can, type Me } from "../auth/access";
import type { Role } from "../auth/admin";
import { type Messages, useT } from "../i18n";
import { Avatar } from "../ui/Avatar";
import { MetricCard } from "../ui/Card";
import { type Column, DataTable } from "../ui/DataTable";
import { DotStatus, Pill } from "../ui/status";
import { day } from "./format";

const selectClass =
  "rounded-md border border-gray-300 bg-transparent px-2 py-1.5 text-sm text-gray-900 focus:border-primary focus:outline-none";

export interface Actions {
  me: Me | undefined;
  roles: Role[];
  toggle: (u: User) => void;
  reset: (u: User) => void;
  invite: (u: User) => void;
  deviceCode: (u: User) => void;
  assign: (u: User, roleId: string) => void;
}

function RoleCell({ user, a }: { user: User; a: Actions }) {
  const t = useT().users.list;
  if (!can(a.me, "AssignUserRole") || a.roles.length === 0) {
    return <Pill tone={user.role === "Super Admin" ? "primary" : "gray"} icon={user.role === "Super Admin" ? <ShieldStar weight="duotone" /> : undefined}>{user.role}</Pill>;
  }
  return (
    <select aria-label={t.roleOf(user.email)} className={selectClass} value={user.role_id} onChange={(e) => a.assign(user, e.target.value)}>
      {a.roles.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
    </select>
  );
}

function columns(t: Messages["users"]["list"], a: Actions): Column<User>[] {
  return [
    {
      key: "user",
      header: t.user,
      cell: (u) => (
        <div className="flex items-center gap-3">
          <Avatar email={u.email} soft />
          <div className="min-w-0">
            <div className="truncate font-semibold text-gray-900">{u.display_name}</div>
            <div className="truncate text-xs text-gray-500">{u.email}</div>
          </div>
        </div>
      ),
    },
    { key: "role", header: t.role, hideBelow: "sm", cell: (u) => <RoleCell user={u} a={a} /> },
    { key: "added", header: t.added, hideBelow: "lg", cell: (u) => day(u.created_at) },
    { key: "password", header: t.password, hideBelow: "md", cell: (u) => (u.has_password ? day(u.password_changed_at) : t.invited) },
    {
      key: "status",
      header: t.status,
      hideBelow: "sm",
      cell: (u) => <DotStatus tone={u.disabled_at == null ? "green" : "gray"} label={u.disabled_at == null ? t.active : t.disabled} />,
    },
    {
      key: "actions",
      header: t.enabled,
      className: "text-end",
      cell: (u) => (
        <div className="flex items-center justify-end gap-3">
          {can(a.me, "InviteUser") ? (
            <ActionIcon size="sm" variant="outline" aria-label={t.invitationLinkFor(u.email)} title={t.invitationLink} onClick={() => a.invite(u)}>
              <LinkSimple weight="bold" className="h-4 w-4" />
            </ActionIcon>
          ) : null}
          {can(a.me, "UpdateUser") ? (
            <ActionIcon size="sm" variant="outline" aria-label={t.issueCodeFor(u.email)} title={t.issueCode} onClick={() => a.deviceCode(u)}>
              <DeviceMobile weight="duotone" className="h-4 w-4" />
            </ActionIcon>
          ) : null}
          {can(a.me, "ResetUserPassword") ? (
            <ActionIcon size="sm" variant="outline" aria-label={t.resetPasswordOf(u.email)} title={t.resetPassword} onClick={() => a.reset(u)}>
              <Key weight="duotone" className="h-4 w-4" />
            </ActionIcon>
          ) : null}
          {can(a.me, "DisableUser") && u.id !== a.me?.id ? (
            <Switch size="sm" role="switch" aria-label={t.canSignIn(u.email)} checked={u.disabled_at == null} onChange={() => a.toggle(u)} />
          ) : null}
        </div>
      ),
    },
  ];
}

export function UsersTable({ users, actions }: { users: User[]; actions: Actions }) {
  const t = useT().users.list;
  return <DataTable label={t.tableLabel} columns={columns(t, actions)} rows={users} rowKey={(u) => u.id} />;
}

export function UserCounts({ users }: { users: User[] }) {
  const t = useT().users.counts;
  const active = users.filter((u) => u.disabled_at == null);
  return (
    <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
      <MetricCard title={t.users} metric={users.length} icon={<UsersThree weight="duotone" />} iconClassName="bg-primary-lighter text-primary-dark" />
      <MetricCard title={t.canSignIn} metric={active.length} icon={<CheckCircle weight="duotone" />} iconClassName="bg-green-lighter text-green-dark" />
      <MetricCard
        title={t.invited}
        metric={active.filter((u) => !u.has_password).length}
        icon={<EnvelopeSimple weight="duotone" />}
        iconClassName="bg-orange-lighter text-orange-dark"
      />
      <MetricCard title={t.disabled} metric={users.length - active.length} icon={<Prohibit weight="duotone" />} iconClassName="bg-gray-100 text-gray-600" />
    </div>
  );
}
